import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Clock, MessageSquare, XCircle,
  Lightbulb, Info, AlertTriangle, ChevronRight, Loader2,
  Phone, Zap, Target, Activity
} from 'lucide-react';

export default function DashboardPage() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      const data = await api.get('/api/dashboard/metrics');
      setMetrics(data);
    } catch (err) {
      toast.error('Error al cargar métricas');
    }
    setLoading(false);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatDate = (d) => {
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    } catch { return d; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="dashboard-loading">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  if (!metrics) return null;

  const maxChartVal = Math.max(...metrics.chart_data.map(d => Math.max(d.sessions, d.suggestions)), 1);

  return (
    <div className="p-6 lg:p-8 max-w-7xl" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Panel de Control
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
          Métricas de rendimiento y resumen de actividad
        </p>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPICard
          icon={Phone}
          label="Sesiones Totales"
          value={metrics.total_sessions}
          sub={`${metrics.completed_sessions} completadas`}
          color="#6366f1"
        />
        <KPICard
          icon={Clock}
          label="Duración Promedio"
          value={formatDuration(metrics.avg_duration_seconds)}
          sub={`${metrics.total_words.toLocaleString()} palabras`}
          color="#22c55e"
        />
        <KPICard
          icon={MessageSquare}
          label="Sugerencias Totales"
          value={metrics.total_suggestions}
          sub={`${metrics.high_urgency_count} alta urgencia`}
          color="#f59e0b"
        />
        <KPICard
          icon={Target}
          label="Oportunidades"
          value={metrics.opportunity_count}
          sub={`Tasa de aceptación: ${metrics.acceptance_rate || 0}%`}
          color="#3b82f6"
        />
      </div>

      {/* Charts + Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Activity Chart */}
        <div
          className="lg:col-span-2 rounded-xl border p-6"
          style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
          data-testid="activity-chart"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Actividad Reciente
            </h3>
            <div className="flex items-center gap-4 text-[10px]" style={{ color: 'var(--text-muted)' }}>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#6366f1' }} /> Sesiones</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#f59e0b' }} /> Sugerencias</span>
            </div>
          </div>
          {metrics.chart_data.length > 0 ? (
            <div className="flex items-end gap-2 h-40">
              {metrics.chart_data.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex items-end justify-center gap-1" style={{ height: '120px' }}>
                    <div
                      className="w-3 rounded-t-sm transition-all duration-500"
                      style={{
                        height: `${Math.max(4, (d.sessions / maxChartVal) * 120)}px`,
                        backgroundColor: '#6366f1',
                      }}
                      title={`${d.sessions} sesiones`}
                    />
                    <div
                      className="w-3 rounded-t-sm transition-all duration-500"
                      style={{
                        height: `${Math.max(4, (d.suggestions / maxChartVal) * 120)}px`,
                        backgroundColor: '#f59e0b',
                      }}
                      title={`${d.suggestions} sugerencias`}
                    />
                  </div>
                  <span className="text-[9px] font-mono" style={{ color: 'var(--text-muted)' }}>
                    {formatDate(d.date)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-40" style={{ color: 'var(--text-muted)' }}>
              <p className="text-sm">Sin datos de actividad</p>
            </div>
          )}
        </div>

        {/* Suggestion Breakdown */}
        <div
          className="rounded-xl border p-6"
          style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
          data-testid="suggestion-breakdown"
        >
          <h3 className="font-heading font-semibold text-sm mb-5" style={{ color: 'var(--text-primary)' }}>
            Desglose de Sugerencias
          </h3>
          <div className="space-y-4">
            <BreakdownItem
              icon={XCircle}
              label="Errores de Datos"
              count={metrics.error_count}
              total={metrics.total_suggestions}
              color="#ef4444"
            />
            <BreakdownItem
              icon={Lightbulb}
              label="Oportunidades"
              count={metrics.opportunity_count}
              total={metrics.total_suggestions}
              color="#f59e0b"
            />
            <BreakdownItem
              icon={Info}
              label="Consejos"
              count={metrics.tip_count}
              total={metrics.total_suggestions}
              color="#3b82f6"
            />
            <BreakdownItem
              icon={AlertTriangle}
              label="Alta Urgencia"
              count={metrics.high_urgency_count}
              total={metrics.total_suggestions}
              color="#ef4444"
            />
            <div className="pt-2 mt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                <span>Aceptadas: <span className="font-bold text-green-400">{metrics.accepted_count || 0}</span></span>
                <span>Descartadas: <span className="font-bold text-red-400">{metrics.dismissed_count || 0}</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Sessions */}
      <div
        className="rounded-xl border p-6"
        style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
        data-testid="recent-sessions"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
            Sesiones Recientes
          </h3>
          <button
            onClick={() => navigate('/sessions')}
            className="text-xs font-medium flex items-center gap-1 transition-colors hover:opacity-80"
            style={{ color: 'var(--accent-primary)' }}
            data-testid="view-all-sessions-btn"
          >
            Ver todas <ChevronRight size={12} />
          </button>
        </div>
        <div className="space-y-2">
          {metrics.recent_sessions.map((s) => (
            <div
              key={s.id}
              onClick={() => navigate(`/sessions/${s.id}`)}
              className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all hover:translate-x-1"
              style={{ backgroundColor: 'var(--surface-secondary)' }}
              data-testid={`recent-session-${s.id}`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`w-2 h-2 rounded-full ${s.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`}
                />
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {s.agent_name}
                </span>
                <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                  {s.created_at ? new Date(s.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                <span>{s.word_count} palabras</span>
                <span className="font-mono" style={{ color: 'var(--accent-primary)' }}>{s.suggestion_count} sug.</span>
                <ChevronRight size={14} />
              </div>
            </div>
          ))}
          {metrics.recent_sessions.length === 0 && (
            <p className="text-sm text-center py-6" style={{ color: 'var(--text-muted)' }}>Sin sesiones recientes</p>
          )}
        </div>
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, color }) {
  return (
    <div
      className="rounded-xl border p-5 transition-all duration-200 hover:translate-y-[-2px] hover:shadow-lg"
      style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={18} style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-heading font-bold" style={{ color: 'var(--text-primary)' }}>{value}</p>
      <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>{label}</p>
      {sub && <p className="text-[10px] mt-1" style={{ color }}>{sub}</p>}
    </div>
  );
}

function BreakdownItem({ icon: Icon, label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Icon size={13} style={{ color }} />
          <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</span>
        </div>
        <span className="text-xs font-mono font-bold" style={{ color }}>{count}</span>
      </div>
      <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--surface-secondary)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
